<template>
  <div class="ms-container w-100" ref="containerRef">
    <button
      type="button"
      :class="[buttonClass || 'btn btn-sm btn-outline-primary', 'ms-toggle w-100', { disabled }]"
      @mousedown.prevent="onTriggerMousedown"
      :disabled="disabled"
    >
      <span v-if="internalValue.length === 0" class="text-muted">{{ placeholder || $t('common.selectOption') }}</span>
      <span v-else-if="simpleDisplay" class="ms-simple-text">
        {{ displayText }}
      </span>
      <span v-else class="ms-badges">
        <span v-for="val in internalValue" :key="val" class="badge bg-primary-subtle text-primary me-1">
          <span class="ms-dot me-1" :style="{ background: optionColor(val) }" v-if="optionColor(val)"></span>
          {{ optionText(val) }}
          <i class="ph-duotone ph-x ms-1 ms-remove" @click.stop="removeByValue(val)"></i>
        </span>
      </span>
      <i class="ti ti-chevron-down ms-caret"></i>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        class="dropdown-menu p-2 ms-menu ms-menu-teleport"
        :class="{ 'dropdown-menu-end': end }"
        :style="teleportMenuStyle"
        @mousedown.stop
        @click.stop
      >
      <div v-if="searchable" class="input-group input-group-sm mb-2">
        <span class="input-group-text"><i class="ti ti-search"></i></span>
        <input ref="searchInputRef" type="text" class="form-control" v-model="query" :placeholder="searchPlaceholder || $t('common.search.placeholder')" />
      </div>
      <div class="ms-options">
        <div v-if="filteredOptions.length === 0" class="ms-empty">{{ $t('common.noData') }}</div>
        <div v-else>
          <div v-for="opt in filteredOptions" :key="opt.value" class="form-check form-check-sm d-flex align-items-center py-1">
            <input class="form-check-input me-2" type="checkbox" :id="`ms-${uid}-${opt.value}`" :checked="isChecked(opt.value)" @change="toggleValue(opt.value)" />
            <label class="form-check-label w-100 d-flex align-items-center" :for="`ms-${uid}-${opt.value}`">
              <span class="ms-dot me-2" :style="{ background: opt.color }" v-if="opt.color"></span>
              <span>{{ opt.text }}</span>
            </label>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-between mt-2">
        <button class="btn btn-sm btn-outline-secondary" type="button" @click="clear" :disabled="internalValue.length===0">{{ $t('common.clear') }}</button>
        <button class="btn btn-sm btn-primary" type="button" @click="close">{{ $t('common.actions.done') }}</button>
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  options: { type: Array, default: () => [] }, // [{ value, text }]
  modelValue: { type: Array, default: () => [] }, // array of values
  placeholder: { type: String, default: '' },
  searchable: { type: Boolean, default: true },
  searchPlaceholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  end: { type: Boolean, default: true },
  maxWidth: { type: Number, default: 320 },
  simpleDisplay: { type: Boolean, default: false }, // If true, show simple text instead of badges
  buttonClass: { type: String, default: '' } // Custom button class (e.g., 'form-select')
})

const emit = defineEmits(['update:modelValue', 'close'])
const containerRef = ref(null)
const menuPosition = ref({ top: 0, left: 0, right: null })

// Dropdown is teleported to body; must be above modals (z-index 1055) but below SweetAlert (~1060+)
const teleportMenuStyle = computed(() => ({
  position: 'fixed',
  top: `${menuPosition.value.top}px`,
  left: menuPosition.value.left !== null ? `${menuPosition.value.left}px` : 'auto',
  right: menuPosition.value.right !== null ? `${menuPosition.value.right}px` : 'auto',
  zIndex: 1056
}))

const open = ref(false)
const query = ref('')
const uid = Math.random().toString(36).slice(2)
const searchInputRef = ref(null)
const internalValue = ref([...props.modelValue])

function updateMenuPosition() {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  menuPosition.value = {
    top: rect.bottom + 4,
    left: props.end ? null : rect.left,
    right: props.end ? window.innerWidth - rect.right : null
  }
}

function onTriggerMousedown(e) {
  if (props.disabled) return
  e.preventDefault()
  open.value = !open.value
  if (open.value) nextTick(() => { updateMenuPosition(); searchInputRef.value?.focus() })
}

watch(() => props.modelValue, (v) => {
  internalValue.value = [...v]
})

const optionMap = computed(() => {
  const map = new Map()
  for (const o of props.options) map.set(o.value, { text: o.text, color: o.color })
  return map
})

const optionText = (v) => optionMap.value.get(v)?.text || String(v)
const optionColor = (v) => optionMap.value.get(v)?.color || null

// Display text for simple mode
const displayText = computed(() => {
  if (internalValue.value.length === 0) return ''
  if (internalValue.value.length === 1) {
    return optionText(internalValue.value[0])
  }
  // Show first item + count
  const firstText = optionText(internalValue.value[0])
  const additionalCount = internalValue.value.length - 1
  return `${firstText} (+${additionalCount})`
})

// Exclude null/empty options so we don't render empty checkboxes
const validOptions = computed(() =>
  (props.options || []).filter(
    (o) => o != null && String(o.value ?? '').trim() !== '' && String(o.text ?? '').trim() !== ''
  )
)

const filteredOptions = computed(() => {
  const term = query.value.toLowerCase()
  if (!term) return validOptions.value
  return validOptions.value.filter((o) => (o.text || '').toLowerCase().includes(term))
})

const close = () => { 
  if (open.value) {
    open.value = false
    emit('close', [...internalValue.value])
  }
}
const isChecked = (val) => internalValue.value.includes(val)
const toggleValue = (val) => {
  const idx = internalValue.value.indexOf(val)
  if (idx === -1) internalValue.value.push(val); else internalValue.value.splice(idx, 1)
  emit('update:modelValue', [...internalValue.value])
}
const removeByValue = (val) => {
  const idx = internalValue.value.indexOf(val)
  if (idx !== -1) internalValue.value.splice(idx, 1)
  emit('update:modelValue', [...internalValue.value])
}
const clear = () => {
  internalValue.value = []
  emit('update:modelValue', [])
}

const handleOutsideMousedown = (e) => {
  if (!open.value) return
  const inContainer = e.target.closest('.ms-container')
  const inMenu = e.target.closest('.ms-menu-teleport')
  if (!inContainer && !inMenu) {
    open.value = false
    emit('close', [...internalValue.value])
  }
}

watch(open, (isOpen) => {
  if (isOpen) nextTick(updateMenuPosition)
})

function onScrollOrResize() {
  if (open.value) nextTick(updateMenuPosition)
}

onMounted(() => {
  document.addEventListener('mousedown', handleOutsideMousedown)
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
})
onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideMousedown)
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
})
</script>

<style scoped>
.ms-container { position: relative; display: inline-block; }
.ms-toggle { position: relative; width: 100%; max-width: v-bind(maxWidth + 'px'); overflow: hidden; text-overflow: ellipsis; white-space: normal; padding-right: 24px; }
.ms-toggle.form-select { min-width: 200px; max-width: 250px; }
.ms-simple-text { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ms-badges { display: block; white-space: normal; gap: 6px; max-width: calc(v-bind(maxWidth + 'px') - 50px); vertical-align: middle; }
.ms-badges > .badge { display: block; margin: 0 0 6px 0 !important; }
.ms-badges > .badge:last-child { margin-bottom: 0 !important; }
.ms-remove { cursor: pointer; }
.ms-caret { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); pointer-events: none; }
.ms-toggle.form-select .ms-caret { display: none; } /* Hide custom caret when using form-select (Bootstrap has its own) */
.ms-menu { 
  min-width: v-bind(maxWidth + 'px');
  max-width: min(360px, calc(100vw - 24px));
  max-height: min(320px, calc(100vh - 120px));
  overflow: auto; 
  display: block; 
  position: absolute; 
  word-wrap: break-word; 
  overflow-wrap: break-word;
}
.ms-options { max-height: 220px; overflow-y: auto; }
.ms-empty { text-align: center; padding: 1rem 0.5rem; color: var(--bs-secondary-color); font-size: 0.875rem; }
.ms-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
</style>


